import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, X, Loader2, Filter, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Persistent left column for the Library page.
 * Every entity bucket is collapsible and lazy-loaded; clicking a row
 * toggles that filter on/off (single-value per bucket). An "active
 * filters" chip strip lives at the top for one-click removal.
 *
 * Props:
 *   filters — { event, rider, horse, owner, photographer, videographer, announcer, gallery }
 *   onChange({ key, value }) — called with which bucket to update and to what value ("" clears)
 *   onClearAll() — clears every entity filter (leaves category/search alone)
 */
export default function LibraryFilterSidebar({ filters, onChange, onClearAll }) {
  const BUCKETS = [
    { key: "association",  label: "Associations",  endpoint: "/associations",           valueKey: "name", extract: (d) => (d.associations || []) },
    { key: "rider",        label: "Riders",        endpoint: "/riders",                 valueKey: "name", extract: (d) => (d.riders || []) },
    { key: "horse",        label: "Horses",        endpoint: "/horses",                 valueKey: "name", extract: (d) => (d.horses || []) },
    { key: "owner",        label: "Owners",        endpoint: "/owners",                 valueKey: "name", extract: (d) => (d.owners || []) },
    { key: "photographer", label: "Photographers", endpoint: "/directory/photographer", valueKey: "name", extract: (d) => (Array.isArray(d) ? d : []) },
    { key: "videographer", label: "Videographers", endpoint: "/directory/videographer", valueKey: "name", extract: (d) => (Array.isArray(d) ? d : []) },
    { key: "announcer",    label: "Announcers",    endpoint: "/directory/announcer",    valueKey: "name", extract: (d) => (Array.isArray(d) ? d : []) },
    { key: "gallery",      label: "Galleries",     endpoint: "/galleries",              valueKey: "id",   extract: (d) => (d.galleries || []) },
  ];

  // Open state per bucket. Whichever bucket currently has an active filter
  // is auto-expanded so the user can see what's on.
  const [openBuckets, setOpenBuckets] = useState(() => {
    const s = new Set(["association"]); // associations open by default (top of the list)
    for (const b of BUCKETS) {
      if (filters?.[b.key]) s.add(b.key);
    }
    return s;
  });
  // rows per bucket: { [key]: Array<{name, id?, clip_count?}> }
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState({});

  const loadBucket = async (b) => {
    if (rows[b.key]) return;
    setLoading((m) => ({ ...m, [b.key]: true }));
    try {
      const r = await api.get(b.endpoint);
      const list = b.extract(r.data);
      setRows((m) => ({ ...m, [b.key]: list }));
    } catch (_) {
      setRows((m) => ({ ...m, [b.key]: [] }));
    } finally {
      setLoading((m) => ({ ...m, [b.key]: false }));
    }
  };

  // Warm the initially-open buckets + any bucket that starts with an
  // active filter (so we can look up the display name for the gallery id).
  useEffect(() => {
    for (const b of BUCKETS) {
      if (openBuckets.has(b.key) || filters?.[b.key]) loadBucket(b);
    }
    // eslint-disable-next-line
  }, []);

  const toggleBucket = (key) => {
    setOpenBuckets((prev) => {
      const n = new Set(prev);
      if (n.has(key)) {
        n.delete(key);
      } else {
        n.add(key);
        const b = BUCKETS.find((x) => x.key === key);
        if (b) loadBucket(b);
      }
      return n;
    });
  };

  const pickRow = (b, row) => {
    const raw = b.valueKey === "id" ? row.id : row.name;
    const active = filters?.[b.key] === raw;
    onChange({ key: b.key, value: active ? "" : raw });
  };

  // Helper — display value for a chip (for gallery id → gallery name lookup).
  const chipLabel = (b) => {
    const v = filters?.[b.key];
    if (!v) return null;
    if (b.valueKey === "id") {
      const g = (rows[b.key] || []).find((r) => r.id === v);
      return g?.name || "Gallery";
    }
    return v;
  };

  const activeChips = BUCKETS
    .map((b) => ({ b, val: filters?.[b.key], display: chipLabel(b) }))
    .filter((x) => x.val);

  return (
    <aside
      className="w-56 shrink-0 border-r border-stone-800 bg-stone-950 py-4 sticky top-0 self-start h-[calc(100vh-0px)] overflow-y-auto"
      data-testid="library-filter-sidebar"
    >
      <div className="px-4 mb-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-500">
          <Filter size={11} className="text-orange-500" />
          Filter By
        </div>
        {activeChips.length > 0 && (
          <button
            onClick={onClearAll}
            className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-orange-400 inline-flex items-center gap-1"
            title="Clear all filters"
            data-testid="sidebar-clear-all"
          >
            <RotateCcw size={10} /> Clear
          </button>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className="px-4 mb-4 flex flex-wrap gap-1.5">
          {activeChips.map(({ b, display }) => (
            <button
              key={b.key}
              onClick={() => onChange({ key: b.key, value: "" })}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider bg-orange-900/40 border border-orange-700 text-orange-300 hover:bg-orange-900/70"
              title={`Remove ${b.label} filter`}
              data-testid={`sidebar-chip-${b.key}`}
            >
              <span className="truncate max-w-[130px]">{display}</span>
              <X size={10} />
            </button>
          ))}
        </div>
      )}

      {BUCKETS.map((b) => {
        const open = openBuckets.has(b.key);
        const list = rows[b.key] || [];
        const isLoading = !!loading[b.key];
        const activeVal = filters?.[b.key];
        return (
          <div key={b.key} className="border-t border-stone-800/70" data-testid={`sidebar-bucket-${b.key}`}>
            <button
              onClick={() => toggleBucket(b.key)}
              className={`w-full flex items-center justify-between px-4 py-2 text-xs uppercase tracking-widest ${
                activeVal ? "text-orange-400" : "text-stone-300"
              } hover:bg-stone-900 transition-colors`}
              data-testid={`sidebar-bucket-toggle-${b.key}`}
            >
              <span className="inline-flex items-center gap-1.5">
                {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                {b.label}
                {activeVal && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
              </span>
              {rows[b.key] != null && (
                <span className="text-[10px] font-mono-tc text-stone-500">{list.length}</span>
              )}
            </button>
            {open && (
              <div className="pb-2">
                {isLoading ? (
                  <div className="px-4 py-2 text-xs text-stone-500 inline-flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Loading…
                  </div>
                ) : list.length === 0 ? (
                  <div className="px-4 py-2 text-xs text-stone-600">None yet</div>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {list.map((row, i) => {
                      const raw = b.valueKey === "id" ? row.id : row.name;
                      const active = activeVal === raw;
                      return (
                        <button
                          key={row.id || row.name || i}
                          onClick={() => pickRow(b, row)}
                          className={`w-full text-left px-4 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                            active
                              ? "text-orange-300 bg-orange-950/40 border-l-2 border-orange-500"
                              : "text-stone-400 hover:bg-stone-900 hover:text-stone-200 border-l-2 border-transparent"
                          }`}
                          data-testid={`sidebar-row-${b.key}-${(row.id || row.name || i).toString().replace(/\s+/g, "_")}`}
                          title={row.name}
                        >
                          <span className="truncate flex-1">{row.name}</span>
                          {row.clip_count != null && (
                            <span className="text-[10px] font-mono-tc text-stone-500 shrink-0">{row.clip_count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
}
